import * as _ from 'lodash';

import { YesOrNo } from '../../Models/IMapItem';
import { IOpening } from '../../Models/IOpening';
import { IRoom } from '../../Models/IRoom';
import { IRoomItem } from '../../Models/IRoomItem';
import { CoordPoint, Wall } from '../../Utils/Types';
import { LaizeCalculatorResult, LaizeProps, ProductType } from './Laize';
import { CalculateOptimalBandsLayoutParams, LaizeV3 } from './v3/LaizeV3';
import { MapHelper } from 'Plugins/FloorPlan/Helpers/MapHelper';
import { ProductHelper } from 'Helpers';

export type ILaizeRoom = {
    roomId?: string;
    flooringWallStart?: Wall;
    coords?: Array<CoordPoint>;
    walls?: Array<ILaizeWall>;
    remonteesPlinthesMarginCm?: number;
};

export type ILaizeOpening = {
    openingId?: string;
    angle?: number;
    limit?: Array<CoordPoint>;
    x?: number;
    y?: number;
    roomId?: string;
    size?: number;
};

export type ILaizeRoomItem = {
    roomId?: string;
    coordsReal?: Array<CoordPoint>;
    putFlooring?: YesOrNo;
};

export type ILaizeWall = {
    roomId?: string;
    start: CoordPoint;
    end: CoordPoint;
};

export type CalculLaizeParams = {
    laizeCalcProps: LaizeCalcProps;

    rooms: Array<IRoom>;
    openings: Array<IOpening>;
    roomItems: Array<IRoomItem>;
    walls: Array<Wall>;

    logName?: string;
    requestedBy?: string;

    iteration?: number;
    forceFlooringWallStart?: Wall;
};

export type CalculLaizeStairsParams = {
    laizeCalcProps: LaizeCalcProps;
    rooms: Array<IRoom & {staircaseIndex: number}>;
    logName?: string;
    requestedBy?: string;
};

export type LaizeCalcProps = {
    LAIZE_PROPS: LaizeProps;

    TYPE?: ProductType;
    LAIZE_MARGIN: number;
    LAIZE_CONSECUTIVE_BANDS_MARGIN: number;

    RACCORD_SIZE: number;
    MARGIN_LENGTH: number;
    MARGIN_WIDTH: number;
    BAND_WIDTH: number;
    LONGUEUR_CM: number;
};

export class LaizeCalc {
    public static calculLaize(params: CalculLaizeParams): LaizeCalculatorResult | undefined {
        return LaizeV3.calculLaizeV3({ ...params });
    }

    public static checkIfLaizeNotComputable(laizeProps: LaizeProps) {
        const laizeRelevantProps = {
            largeurCm: laizeProps?.largeurCm,
            longueurCm: laizeProps?.longueurCm,
            raccordCm: laizeProps?.raccordCm,
            largeurMarginCm: laizeProps?.largeurMarginCm,
            longueurMarginCm: laizeProps?.longueurMarginCm,
        };

        return _.isEqual(laizeRelevantProps, {
            largeurCm: 0,
            longueurCm: 0,
            raccordCm: 0,
            largeurMarginCm: 0,
            longueurMarginCm: 0,
        });
    }

    public static toLaizeCalcProps = (laizeProps: LaizeProps): LaizeCalcProps => {
        return {
            LAIZE_PROPS: laizeProps,

            //* use in v2
            TYPE: laizeProps?.type,

            LAIZE_CONSECUTIVE_BANDS_MARGIN: laizeProps?.type === ProductType.Rouleau ? 10 : 0,
            LAIZE_MARGIN: laizeProps?.type === ProductType.Rouleau ? 10 : 0,

            RACCORD_SIZE: (laizeProps?.type === ProductType.Rouleau ? laizeProps.raccordCm : 0) || 0,
            MARGIN_LENGTH: (laizeProps?.type === ProductType.Rouleau ? laizeProps.longueurMarginCm : 0) || 0,
            MARGIN_WIDTH: (laizeProps?.type === ProductType.Rouleau ? laizeProps.largeurMarginCm : 0) || 0,
            BAND_WIDTH: laizeProps.largeurCm || 400,
            LONGUEUR_CM: laizeProps.longueurCm || 50_000,
        };
    };

    public static toCalculateOptimalBandsLayoutParams = (
        laizeParams: CalculLaizeParams
    ): CalculateOptimalBandsLayoutParams => {
        const { laizeCalcProps, openings, roomItems, rooms, walls, forceFlooringWallStart } = laizeParams;
        const laizeOpenings = openings.map(
            (x) =>
                ({
                    roomId: x.roomId!,
                    angle: x.angle,
                    limit: x.limit,
                    openingId: x.openingId,
                    size: x.size,
                    x: x.x,
                    y: x.y,
                } as ILaizeOpening)
        );
        const laizeRoomItems = roomItems.map(
            (x) =>
                ({
                    roomId: x.roomId!,
                    coordsReal: x.coordsReal,
                    putFlooring: x.putFlooring,
                } as ILaizeRoomItem)
        );
        const convertWalls = (walls?: Array<Wall>) =>
            walls?.map(
                (x) =>
                    ({
                        roomId: x.roomId!,
                        start: x.start,
                        end: x.end,
                    } as ILaizeWall)
            ) ?? [];
        const laizeWalls = convertWalls(walls);
        const laizeRooms = rooms.map((x) => {
            const remonteesPlinthesMarginCm = ProductHelper.getEvaluationsFromSourceQuestion(x, 'RMT_PLINTHES');
            return {
                coords: x.coords,
                flooringWallStart: laizeParams.forceFlooringWallStart ?? x.flooringWallStart,
                roomId: x.roomId,
                walls: convertWalls(x.walls),
                remonteesPlinthesMarginCm: parseInt(remonteesPlinthesMarginCm, 10) || undefined,
            } as ILaizeRoom;
        });
        return {
            laizeCalcProps,
            openings: laizeOpenings,
            roomItems: laizeRoomItems,
            rooms: laizeRooms,
            walls: laizeWalls,
            forceFlooringWallStart,
        };
    };

    public static getLayLamePleinePossibleSizeOnEachWalls = (
        laizeProps: LaizeCalcProps,
        room: IRoom,
        allRooms: Array<IRoom>
    ) => {
        const { rooms, openings, roomItems, walls } = MapHelper.getRoomAllElements(allRooms);
        const lamePleinePossibleSizes: Array<{ wallId: string; size: number }> = [];
        for (const wall of room.walls) {
            const result = LaizeV3.calculLaizeV3({
                laizeCalcProps: laizeProps,
                rooms: rooms,
                openings: openings,
                roomItems: roomItems,
                walls: walls,
                forceFlooringWallStart: wall,
            });
            if (result) {
                lamePleinePossibleSizes.push({ wallId: wall.wallId, size: result.laizeResult });
            }
        }
        return lamePleinePossibleSizes;
    };
}
